import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './TailwindComponents/Button';
import FileUploader from './TailwindComponents/FileUploader';
import Textarea from './TailwindComponents/Textarea';
import { FIELD_HELP_CLASS } from './TailwindComponents/FieldShell';
import FieldError from './FormComponents/FieldError';
import { MAX_UPLOAD_BYTES } from '../../utils/uploadLimits';

export type FileOrDescriptionMode = 'files' | 'description';

export interface FileOrDescriptionValue {
  files: File[];
  description: string;
}

export interface FileOrDescriptionSelectorProps {
  value?: FileOrDescriptionValue;
  onChange?: (value: FileOrDescriptionValue) => void;
  files?: File[];
  description?: string;
  onFilesChange?: (files: File[]) => void;
  onDescriptionChange?: (description: string) => void;
  onModeChange?: (mode: FileOrDescriptionMode) => void;
  error?: string;
  allowedExtensions?: string[];
  maxFileSize?: number;
}

const FileOrDescriptionSelector: React.FC<FileOrDescriptionSelectorProps> = ({
  value,
  onChange,
  files: legacyFiles,
  description: legacyDescription,
  onFilesChange: legacyOnFilesChange,
  onDescriptionChange: legacyOnDescriptionChange,
  onModeChange,
  error,
  allowedExtensions,
  maxFileSize = MAX_UPLOAD_BYTES,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<FileOrDescriptionMode>('files');

  const files = value?.files ?? legacyFiles ?? [];
  const description = value?.description ?? legacyDescription ?? '';

  // Refs to keep callbacks always-current (avoids stale closures in async FileUploader)
  // Set directly during render (not via useEffect) to eliminate any timing gap
  const filesRef = useRef(files);
  filesRef.current = files;
  const descriptionRef = useRef(description);
  descriptionRef.current = description;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const legacyOnFilesChangeRef = useRef(legacyOnFilesChange);
  legacyOnFilesChangeRef.current = legacyOnFilesChange;
  const legacyOnDescriptionChangeRef = useRef(legacyOnDescriptionChange);
  legacyOnDescriptionChangeRef.current = legacyOnDescriptionChange;

  const handleFilesChange = useCallback((newFiles: File[]) => {
    if (onChangeRef.current) {
      onChangeRef.current({ files: newFiles, description: descriptionRef.current });
    } else if (legacyOnFilesChangeRef.current) {
      legacyOnFilesChangeRef.current(newFiles);
    }
  }, []);

  const handleDescriptionChange = useCallback((newDescription: string) => {
    if (onChangeRef.current) {
      onChangeRef.current({ files: filesRef.current, description: newDescription });
    } else if (legacyOnDescriptionChangeRef.current) {
      legacyOnDescriptionChangeRef.current(newDescription);
    }
  }, []);

  const handleToggleMode = useCallback(() => {
    const newMode = mode === 'files' ? 'description' : 'files';
    setMode(newMode);
    onModeChange?.(newMode);
  }, [mode, onModeChange]);

  const hasFiles = files.length > 0;
  const hasDescription = description.trim().length > 0;
  const isValid = hasFiles || hasDescription;

  return (
    <div className="space-y-4">
      <div className="space-y-3 flex row justify-between items-center">
        <p className={FIELD_HELP_CLASS}>
          {t('Choose to upload files or write a description of your matter.')}
        </p>
        <Button variant="outline" size="small" onClick={handleToggleMode} type="button">
          {mode === 'files' ? t('Write Description') : t('Upload Files')}
        </Button>
      </div>

      {mode === 'files' ? (
        <FileUploader
          files={files}
          onFilesChange={handleFilesChange}
          allowedExtensions={allowedExtensions}
          maxSizeBytes={maxFileSize}
        />
      ) : (
        <Textarea
          id='matter-desc'
          name="description"
          label={t('Matter Description')}
          placeholder={t('Describe your matter here')}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            handleDescriptionChange(e.target.value)
          }
          value={description}
          error={!isValid ? error : undefined}
          rows={6}
        />
      )}

      {error && !isValid && <FieldError error={error} />}
    </div>
  );
};

export default FileOrDescriptionSelector;
